import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { FileText, Download, LogIn, UserPlus, ArrowRight, Shield, Clock, Users, Loader2, Calculator } from "lucide-react";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import { fetchDemoInfo } from "@/lib/demo";  // DEMO-1d
import { downloadPdf } from "@/lib/downloadPdf";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_HORIZONTAL = "/logos/solara-horizontal.png";
const CALCULATORS_URL = "https://solaraglobal.com.au/calculators/?utm_source=app&utm_medium=calculators_link";

export default function LandingPage() {
  // DEMO-1d — the demo site has no marketing page: send visitors straight to
  // the sign-in page with the "Try the demo" buttons. No-op on production.
  const navigateDemo = useNavigate();
  useEffect(() => {
    let alive = true;
    fetchDemoInfo().then((d) => { if (alive && d) navigateDemo("/login", { replace: true }); });
    return () => { alive = false; };
  }, [navigateDemo]);
  const { user, isAdmin, logout } = useAuth();
  const [isDownloading, setIsDownloading] = useState(false);

  return (
    <div className="min-h-screen bg-[#fafaf9]">
      {/* Header */}
      <header className="bg-[#021d49] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center">
              <img 
                src={LOGO_HORIZONTAL} 
                alt="Solara Global Capital Partners" 
                className="h-12 md:h-14 brightness-0 invert"
                data-testid="logo-header"
              />
            </Link>
            
            <nav className="flex items-center gap-4">
              {user ? (
                <>
                  <span className="hidden md:block text-sm text-gray-300">
                    Welcome, {user.name}
                  </span>
                  {isAdmin && (
                    <Link 
                      to="/admin" 
                      className="text-[#bd9a59] hover:text-white transition-colors text-sm"
                      data-testid="admin-link"
                    >
                      Admin
                    </Link>
                  )}
                  <Link 
                    to="/my-applications" 
                    className="text-gray-300 hover:text-white transition-colors text-sm"
                    data-testid="my-applications-link"
                  >
                    My Applications
                  </Link>
                  <button 
                    onClick={logout}
                    className="text-gray-300 hover:text-white transition-colors text-sm"
                    data-testid="logout-btn"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link 
                    to="/login" 
                    className="text-gray-300 hover:text-white transition-colors text-sm flex items-center gap-1"
                    data-testid="login-link"
                  >
                    <LogIn className="w-4 h-4" />
                    Login
                  </Link>
                  <Link 
                    to="/register" 
                    className="bg-[#bd9a59] hover:bg-[#9c7e46] text-white px-4 py-2 rounded text-sm flex items-center gap-1 transition-colors"
                    data-testid="register-link"
                  >
                    <UserPlus className="w-4 h-4" />
                    Register
                  </Link>
                </>
              )}
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-[#021d49] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center lg:text-left max-w-3xl mx-auto lg:mx-0">
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Business Finance <br />
              <span className="text-[#bd9a59]">Made Simple</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-300 mb-8">
              Apply for business loans, investment finance, and commercial lending with Solara Global Capital Partners. Fast approvals, competitive rates.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Link 
                to="/apply" 
                className="bg-[#bd9a59] hover:bg-[#9c7e46] text-white px-8 py-4 rounded font-medium text-lg inline-flex items-center justify-center gap-2 transition-colors"
                data-testid="start-application-btn"
              >
                Start Application
                <ArrowRight className="w-5 h-5" />
              </Link>
              <button 
                onClick={() => downloadPdf(`${BACKEND_URL}/api/pdf/blank`, "Solara_Loan_Application_Blank.pdf")}
                className="bg-transparent border-2 border-white text-white px-8 py-4 rounded font-medium inline-flex items-center justify-center gap-2 transition-colors hover:bg-white hover:text-[#021d49]"
                data-testid="download-form-btn"
              >
                <Download className="w-5 h-5" />
                Download Printable Form
              </button>
            </div>
            <div className="mt-6 text-center lg:text-left">
              <a
                href={CALCULATORS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#bd9a59] hover:text-white transition-colors text-sm font-medium"
                data-testid="calculators-link-hero"
              >
                <Calculator className="w-4 h-4" />
                Try our loan calculators
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#021d49] text-center mb-12">
            Why Choose Solara Global Capital Partners?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-[#bd9a59]/10 rounded flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-[#bd9a59]" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#021d49] mb-3">Fast Approvals</h3>
              <p className="text-gray-600">
                Get your loan application reviewed within 24-48 hours. We understand time is money for your business.
              </p>
            </div>
            <div className="bg-white p-8 rounded shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-[#bd9a59]/10 rounded flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-[#bd9a59]" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#021d49] mb-3">Secure Process</h3>
              <p className="text-gray-600">
                Your data is protected with bank-level encryption. Apply online with complete peace of mind.
              </p>
            </div>
            <div className="bg-white p-8 rounded shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-[#bd9a59]/10 rounded flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-[#bd9a59]" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#021d49] mb-3">Expert Support</h3>
              <p className="text-gray-600">
                Our experienced team is here to guide you through every step of the application process.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#021d49] text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-gray-300 mb-8 text-lg">
            Apply online in minutes or download a printable form to complete manually.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/apply" 
              className="bg-[#bd9a59] hover:bg-[#9c7e46] text-white px-8 py-4 rounded font-medium flex items-center justify-center gap-2 transition-colors"
            >
              <FileText className="w-5 h-5" />
              Apply Online Now
            </Link>
            <button 
              onClick={() => downloadPdf(`${BACKEND_URL}/api/pdf/blank`, "Solara_Loan_Application_Blank.pdf")}
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded font-medium flex items-center justify-center gap-2 transition-colors hover:bg-white hover:text-[#021d49]"
            >
              <Download className="w-5 h-5" />
              Download PDF Form
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#011230] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center text-center">
            <img 
              src={LOGO_HORIZONTAL} 
              alt="Solara Global Capital Partners" 
              className="h-20 md:h-24 brightness-0 invert mb-4"
            />
            <p className="text-gray-400 text-sm mb-6">
              TRANSPARENCY • SECURITY • INTEGRITY
            </p>
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 mb-6">
              <p className="text-gray-400 text-sm">
                Contact: <a href="mailto:loans@solaraglobal.com.au" className="text-[#bd9a59] hover:underline">loans@solaraglobal.com.au</a>
              </p>
              <p className="text-gray-400 text-sm">
                Website: <a href="https://www.solaraglobal.com.au" target="_blank" rel="noopener noreferrer" className="text-[#bd9a59] hover:underline">www.solaraglobal.com.au</a>
              </p>
              <p className="text-gray-400 text-sm">
                <a href={CALCULATORS_URL} target="_blank" rel="noopener noreferrer" className="text-[#bd9a59] hover:underline" data-testid="calculators-link-footer">Loan Calculators</a>
              </p>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Solara Global Capital Partners. All rights reserved.
            </p>
            <p className="text-gray-500 text-xs mt-3 space-x-3">
              <a href="/privacy" className="hover:text-[#bd9a59] hover:underline">Privacy Policy</a>
              <span className="text-gray-700">·</span>
              <a href="/terms" className="hover:text-[#bd9a59] hover:underline">Terms of Service</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
